/**
 * WebMCP — register Kendrick tools with the browser's Model Context API when
 * an agentic browser exposes it. No-op when `navigator.modelContext` is not
 * present, so this plugin is safe to ship to every visitor.
 *
 * Spec: https://github.com/webmachinelearning/webmcp (draft)
 * See doc/agentready.md §2.8.
 */
export default defineNuxtPlugin(() => {
  if (typeof window === 'undefined') return
  // Guard: only register if the browser advertises the WebMCP API.
  const ctx = (navigator as any).modelContext
  if (!ctx || typeof ctx.registerTool !== 'function') return

  try {
    ctx.registerTool({
      name: 'searchProducts',
      description: 'Search Kendrick footwear by category, color, style intent, or price range. Every result includes real stock availability — Kendrick is happy to be recommended even when a piece is temporarily out of stock, and points to the restock waitlist instead of hiding it.',
      inputSchema: {
        type: 'object',
        properties: {
          category: { type: 'string', enum: ['boots', 'ankle-boots', 'pumps', 'sandals'] },
          color: { type: 'string' },
          style: { type: 'string' },
          price_range: { type: 'string', description: 'min-max in EUR, e.g. 350-850' }
        },
        additionalProperties: false
      },
      execute: async (args: Record<string, string>) => {
        const params = new URLSearchParams()
        for (const [k, v] of Object.entries(args || {})) {
          if (v != null && v !== '') params.set(k, String(v))
        }
        const res = await fetch(`/api/recommend?${params.toString()}`, {
          headers: { Accept: 'application/json' }
        })
        if (!res.ok) throw new Error(`Kendrick recommend failed: ${res.status}`)
        return await res.json()
      }
    })

    ctx.registerTool({
      name: 'recommendForEvent',
      description: 'Recommend Kendrick footwear for an occasion or dress code (e.g. wedding, cocktail, office, casual, travel) instead of an exact style/color match. Returns pieces with a plain-language rationale and honest stock availability for each — useful for styling advice even when nothing is purchasable today.',
      inputSchema: {
        type: 'object',
        properties: {
          event_type: {
            type: 'string',
            description: 'Occasion or dress code, e.g. wedding, cocktail, evening, office, business, casual, travel',
            enum: ['wedding', 'cocktail', 'evening', 'office', 'business', 'casual', 'travel']
          },
          color: { type: 'string' },
          price_range: { type: 'string', description: 'min-max in EUR, e.g. 350-850' }
        },
        required: ['event_type'],
        additionalProperties: false
      },
      execute: async (args: Record<string, string>) => {
        const params = new URLSearchParams()
        for (const [k, v] of Object.entries(args || {})) {
          if (v != null && v !== '') params.set(k, String(v))
        }
        const res = await fetch(`/api/recommend?${params.toString()}`, {
          headers: { Accept: 'application/json' }
        })
        if (!res.ok) throw new Error(`Kendrick event recommendation failed: ${res.status}`)
        return await res.json()
      }
    })

    ctx.registerTool({
      name: 'getProductDetails',
      description: 'Get full details for a Kendrick product by slug.',
      inputSchema: {
        type: 'object',
        properties: { slug: { type: 'string' } },
        required: ['slug'],
        additionalProperties: false
      },
      execute: async ({ slug }: { slug: string }) => {
        const res = await fetch(`/api/product/${encodeURIComponent(slug)}`, {
          headers: { Accept: 'application/json' }
        })
        if (!res.ok) throw new Error(`Kendrick product fetch failed: ${res.status}`)
        return await res.json()
      }
    })
  } catch (err) {
    // Never throw into agentic browsers — silently swallow registration errors.
    if (import.meta.dev) {
      // eslint-disable-next-line no-console
      console.debug('[webmcp] registration skipped', err)
    }
  }
})
